import { CALLBACK_PATH, OAUTH_SCOPES } from '@coworker/contracts';

import type { AppConfig } from '../lib/config.js';
import { challengeFor, createState, createVerifier } from './pkce.js';

/**
 * The Cognito hosted login, from this side - AUTH-003, AUTH-004, ADR-032.
 *
 * `auth-stack.ts` registers exactly two callback URLs and two logout URLs, so
 * every string built here has to match one of them character for character or
 * Cognito refuses the request with an error page rather than a redirect. That is
 * why `redirectUri` is derived from `window.location.origin` and a constant
 * rather than written out per call site.
 *
 * `domain.baseUrl()` is an *origin* - the paths below are appended to it.
 */

export interface StartedLogin {
  readonly url: string;
  readonly verifier: string;
  readonly state: string;
}

export interface Tokens {
  readonly accessToken: string;
  /**
   * Kept, not discarded. An access token carries no email and no name, so
   * AUTH-010 verifies this one server-side to create the `app_user` row -
   * ADR-032. It is the only place those claims exist on this side.
   */
  readonly idToken: string;
  /**
   * There is deliberately **no** `refreshToken` here.
   *
   * It used to be carried into memory and read by nothing. Since COW-483 the
   * refresh token lives in an `HttpOnly` cookie our API sets, so page script
   * cannot reach it - and removing the field is what makes that a guarantee of
   * the type rather than a claim in a comment. Anything that needs a new access
   * token calls `POST /v1/session/refresh`; see `session-api.ts`.
   */
  /** Absolute milliseconds. Cognito answers with a lifetime, not a deadline. */
  readonly expiresAt: number;
}

export const redirectUri = (origin: string): string => `${origin}${CALLBACK_PATH}`;

async function authorizeUrl(
  config: AppConfig,
  origin: string,
  path: '/oauth2/authorize' | '/signup',
): Promise<StartedLogin> {
  const verifier = createVerifier();
  const state = createState();

  const query = new URLSearchParams({
    client_id: config.userPoolClientId,
    response_type: 'code',
    // Exactly the scopes the client is registered for - `auth-stack.spec.ts`
    // asserts the pool grants these and no others. Asking for one it does not
    // have is invalid_scope, not a narrower grant.
    scope: OAUTH_SCOPES.join(' '),
    redirect_uri: redirectUri(origin),
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });

  return { url: `${config.loginUrl}${path}?${query.toString()}`, verifier, state };
}

/** AUTH-004. */
export const startLogin = (config: AppConfig, origin: string): Promise<StartedLogin> =>
  authorizeUrl(config, origin, '/oauth2/authorize');

/**
 * AUTH-003. The hosted UI's own sign-up screen rather than a form of ours: the
 * pool owns the password policy, the email verification and the MFA enrolment,
 * and a second implementation of any of them would be a second thing to get
 * wrong. It redeems through the same callback.
 */
export const startSignUp = (config: AppConfig, origin: string): Promise<StartedLogin> =>
  authorizeUrl(config, origin, '/signup');

/**
 * `logout_uri` must be a registered logout URL, and `auth-stack.ts` registers the
 * site root - not a `/logout` path. Sending anything else produces an error page
 * where a signed-out home page belongs.
 */
export function logoutUrl(config: AppConfig, origin: string): string {
  const query = new URLSearchParams({ client_id: config.userPoolClientId, logout_uri: origin });
  return `${config.loginUrl}/logout?${query.toString()}`;
}

export class TokenExchangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TokenExchangeError';
  }
}

/**
 * The token exchange used to live here, and now lives in the API.
 *
 * `exchangeCode` POSTed the authorization code straight to Cognito from the
 * browser and handed back everything it got, refresh token included. COW-483
 * moved that behind `POST /v1/session` so the refresh token never reaches page
 * script at all - see `session-api.ts` and ADR-0039. What stayed here is
 * everything that is genuinely a *browser* concern: the PKCE challenge, the
 * authorize URL, and the state this tab must check on the way back.
 */
